import { getSiteUrl } from './site-url';

/**
 * Checks if a URL is a Cognito file URL from the current site.
 * @param url - The URL object to check
 * @returns True if the URL is a Cognito file URL from the site
 */
function isCognitoFileUrl(url: URL): boolean {
	// Only allow URLs from the site (uploaded files)
	const siteUrl = getSiteUrl();
	if (!siteUrl || !url.href.startsWith(siteUrl)) {
		return false;
	}

	// Check if this is a permalink file URL (/file/{token})
	if (!url.pathname.match(/\/file\/[^/]+$/)) {
		return false;
	}

	return true;
}

/**
 * Checks if a URL has the redirect=0 parameter.
 * @param url - The URL object to check
 * @returns True if redirect=0 is NOT set (meaning we can add it for retry)
 */
function hasNoRedirectParam(url: URL): boolean {
	return url.searchParams.get('redirect') !== '0';
}

/**
 * Adds the redirect=0 parameter to a URL.
 * @param url - The URL object to modify
 * @returns The modified URL as a string
 */
function addNoRedirectParam(url: URL): void {
	url.searchParams.set('redirect', '0');
}

/**
 * Gets the retry URL for a permalink file URL with redirect disabled.
 * @param url - The URL that failed to load
 * @returns The retry URL with redirect=0, or null if no retry is possible
 */
export function getPermalinkRetryUrl(url: string): string | null;
export function getPermalinkRetryUrl(url: URL): string | null;
export function getPermalinkRetryUrl(url: string | URL): string | null {
	if (!url) return null;

	if (typeof url === 'string') {
		try {
			url = new URL(url);
		}
		catch (e) {
			// Invalid URL
			return null;
		}
	}

	// Check if this is a Cognito file URL that can be retried
	if (!isCognitoFileUrl(url)) {
		return null;
	}

	// Check if we can add the redirect parameter
	if (hasNoRedirectParam(url)) {
		// Add the redirect=0 parameter and return the modified URL
		addNoRedirectParam(url);
		return url.toString();
	}

	return null;
}
